/* eslint-disable consistent-return */

/**
 * @file 交易页通用数据模块
 * @date 2021-07-08
 */

import { ls } from '@sfe/bone-cache';
import { i18n, replace } from '@/locales/i18n';
import { ltApi, userapi, commonapi } from '@/apis/spot';
import { info as infoToast } from '@/utils/toast';

/**
 * 现货交易页缓存的交易对 id
 */
const SPOT_LS_SYMBOL_ID = 'SPOT_symbolid';

export const LS_LT_VIDEO_CACHE_KEY = 'SPOT_has_watched_lt_learning_video';

const state = {
    /**
     * 当前交易页选中的交易对 ID
     *
     * @type {string}
     */
    symbolId: '',

    /**
     * 当前选中交易对的最新行情数据
     *
     * @type {Object}
     */
    klineinfo: {},

    /**
     * 当前所有市场行情（topic=slowBroker），以交易对的 symbolId 为 key
     * 存在这里是因为在交易页好几个地方需要，并且 MarketList.vue 将加载多个地方
     *
     * @type {Object}
     */
    marketMap: {}, // 交易对列表市场行情

    selectedPrice: '', // 已选交易价格
    openLoading: true,
    openPlanLoading: true,
    openStopLimitLoading: true, // 当前订单-止盈止损加载状态
    openOrders: [],
    openPlanOrders: [],
    stopLimitOrders: [], // 当前订单-止盈止损订单列表
    tradeOrders: [],
    tradePlanOrders: [],
    commission: ls.get('SPOT_currentorder_sub_active') || 'ordinary', // 当前订单/历史订单 子tab选中状态
    historyCommission: ls.get('SPOT_historyorder_sub_active') || 'ordinary', // 历史订单子状态
    ltSubTab: ls.get('SPOT_ltorder_sub_active') || 'subscriptionOrder', // lt订单子订单状态
    allPairs: ls.get('SPOT_show_all_trading_pairs') === '1', // 是否显示全部交易对
    buySell: 'buy', // 买卖方向
    orderType: 'limit', // 订单类型

    UTAAllAssets: true, // 订单区-UTA账户是否显示全部资产

    /**
     * 我的订单整个模块高度
     */
    myOrderHeight: 0,

    /**
     * 生成2个默认基础、计价货币的占位，用于解决打开->加载数据之间的 BTC-> -- -> BTC 的抖动
     * 不能直接使用，一定要先使用当前交易对的名称，如：`symbolData.baseTokenName || baseTokenDefault`
     */
    baseTokenDefault: '--',
    quoteTokenDefault: '--',

    /**
     * BIT免责声明状态
     * bitDisclaimer 用户是否知晓
     * bitDisclaimerVisible 提示是否显示
     */
    bitDisclaimer: 0,
    bitDisclaimerVisible: false,

    /**
     * LT状态
     */
    ltKlineInfo: {}, // LT行情信息
    ltRisk: {
        isLT: false, // 是否LT
        isVisible: false, // 是否显示LT弹层
        isConfirmOpen: false, // 风险提示是否确认
        isPassTest: false, // 风险测试题是否通过
        isStrongTipsVisible: false, // 强风险提示
        questions: [], // 风险测试题列表
        hasWatchedLearningVideo: ls.get(LS_LT_VIDEO_CACHE_KEY) === 'true',
    },
    ltSymbolInfo: {}, // 信息披露
    /**
     * ST退市标签
     * stRisk 弹窗是否展示
     */
    stRisk: false,

    /**
     * 创新区状态
     * innovateStatus 用户是否知晓 0否1是
     * innovateRisk   提示是否显示
     */
    innovateStatus: 0,
    innovateRisk: false,

    // 订单表数据
    orderBooks: {
        buyOnePrice: '0', // 买一价格
        sellOnePrice: '0', // 卖一价格
    },

    // 通用提示框
    risk: {
        visible: false,
        title: '',
        content: '',
        confirmText: '',
    },

    // 交易对是否符合KYC规则情况
    kycRule: {
        isKyc: true, // 默认假设已经KYC
        area: [], // 该交易对如果不符合KYC规则，可以交易的地区列表
    },

    /* SUI交易对是否签署协议控制 */
    isSuiVisible: false, // 协议弹层显示控制
    isSuiTermed: false, // 协议是否签署
};

const mutations = {
    UPDATE_LT_SUBTAB(state, payload) {
        ls.set('SPOT_ltorder_sub_active', payload);
        state.ltSubTab = payload;
    },
    SET_BASE_TOKEN_DEFAULT(state, payload) {
        state.baseTokenDefault = payload;
    },
    SET_QUOTE_TOKEN_DEFAULT(state, payload) {
        state.quoteTokenDefault = payload;
    },

    SET_SYMBOL_ID(state, payload) {
        ls.set(SPOT_LS_SYMBOL_ID, payload);
        state.symbolId = payload;
    },
    SET_MARKETLIST(state, payload = []) {
        payload.forEach((item) => {
            state.marketMap[item.s] = item;
        });
    },
    SET_KLINEINFO(state, payload) {
        state.klineinfo = payload || {};
    },
    SET_BIT_DISCLAIMER(state, payload) {
        state.bitDisclaimer = payload;
    },
    SET_BIT_DISCLAIMER_VISIBLE(state, payload) {
        state.bitDisclaimerVisible = payload;
    },
    // 更新已选交易价格
    UPDATE_SELECTED_PRICE(state, price) {
        state.selectedPrice = '';
        state.selectedPrice = price;
    },
    UPDATE_OPEN_ORDERS(state, openOrders) {
        state.openOrders = openOrders;
    },
    UPDATE_OPEN_PLAN_ORDERS(state, openPlanOrders) {
        state.openPlanOrders = openPlanOrders;
    },
    UPDATE_STOP_LIMIT_ORDERS(state, data) {
        state.stopLimitOrders = data;
    },
    UPDATE_TRADE_ORDERS(state, tradeOrders) {
        state.tradeOrders = tradeOrders;
    },
    UPDATE_TRADE_PLAN_ORDERS(state, tradePlanOrders) {
        state.tradePlanOrders = tradePlanOrders;
    },
    UPDATE_COMMISSION(state, commission) {
        ls.set('SPOT_currentorder_sub_active', commission);
        state.commission = commission;
    },
    UPDATE_HISTORY_COMMISSION(state, commission) {
        ls.set('SPOT_historyorder_sub_active', commission);
        state.historyCommission = commission;
    },
    CHANGE_ALL_PAIRS(state) {
        state.allPairs = !state.allPairs;
        ls.set('SPOT_show_all_trading_pairs', state.allPairs ? '1' : '0');
    },
    UPDATE_ORDERS_LOADING(state, bool) {
        state.openLoading = bool;
        state.openPlanLoading = bool;
        state.openStopLimitLoading = bool;
    },
    UPDATE_ORDER_TABLE_HEIGHT(state, height) {
        state.myOrderHeight = height;
    },
    UPDATE_BUY_SELL_TYPE(state, side) {
        state.buySell = side;
    },
    UPDATE_ORDER_TYPE(state, type) {
        state.orderType = type;
    },

    // 更新LT信息
    UPDATE_IS_LT(state, isLT) {
        state.ltRisk.isLT = isLT;
    },
    UPDATE_LT_LAYER(state, visible) {
        state.ltRisk.isVisible = visible;
    },
    UPDATE_LT_RISK(state, isConfirmOpen) {
        state.ltRisk.isConfirmOpen = isConfirmOpen;
    },
    UPDATE_LT_QUESTION_STATE(state, isPassTest) {
        state.ltRisk.isPassTest = isPassTest;
    },
    UPDATE_LT_QUESTIONS(state, questions) {
        state.ltRisk.questions = questions;
    },
    GET_LT_SYMBOL_INFO(state, info) {
        state.ltSymbolInfo = info;
    },
    UPDATE_LT_TIPS(state, visible) {
        state.ltRisk.isStrongTipsVisible = visible;
    },
    UPDATE_LT_VIDEO_STATUS(state) {
        state.ltRisk.hasWatchedLearningVideo = true;
    },
    SET_KLINEINFO_LT(state, payload) {
        state.ltKlineInfo = payload || {};
    },
    SET_ST_RISK(state, payload) {
        state.stRisk = payload;
    },
    SET_INNOVATE_STATUS(state, payload) {
        state.innovateStatus = payload;
    },
    SET_INNOVATE_RISK(state, payload) {
        state.innovateRisk = payload;
    },
    // 实时更新订单表买一卖一数据
    UPDATE_ORDER_BOOKS(state, payload) {
        Object.assign(state.orderBooks, payload);
    },
    // 更新提示框
    UPDATE_RISK(state, payload) {
        Object.assign(state.risk, payload);
    },

    // 交易对是否符合KYC规则
    SET_IS_KYC(state, payload) {
        state.kycRule.isKyc = payload;
    },
    // 交易对不符合KYC规则，可交易的地区
    SET_KYC_AREA(state, payload) {
        state.kycRule.area = payload;
    },

    // SUI交易对协议弹层显示控制
    UPDATE_SUI_VISIBLE(state, payload) {
        state.isSuiVisible = payload;
    },
    // SUI交易对是否签署协议
    UPDATE_SUI_TERMED(state, payload) {
        state.isSuiTermed = payload;
    },

    // 更新UTA账户是否显示全部资产
    UPDATE_UTA_ALL_ASSETS(state) {
        state.UTAAllAssets = !state.UTAAllAssets;
    },
};

const actions = {
    // 特殊说明：
    // repair：是否启用自动修复逻辑，在交易对不存在时，会走交易对兜底逻辑（取缓存中的交易对->第一个可以访问的交易对）；
    // ignoreKYC：忽略KYC校验逻辑；
    async SET_SYMBOL_ID({
        commit, state, rootGetters, dispatch,
    }, {
        baseTokenId, quoteTokenId, repair = true, ignoreKYC = false,
    } = {}) {
        const symbols = rootGetters['settings/symbols'];
        const symbolMap = rootGetters['settings/symbolMap'];
        if (!symbols.length) return;

        // 优先使用参数中的交易对（链接、切换交易时）
        let symbol = baseTokenId && quoteTokenId
            ? symbols.find((item) => (
                item.baseTokenId.toUpperCase() === baseTokenId.toUpperCase()
                    && item.quoteTokenId.toUpperCase() === quoteTokenId.toUpperCase()
            ))
            : null;

        // 获取地址栏中的交易对信息
        const linkSymbolId = baseTokenId && quoteTokenId ? `${baseTokenId}${quoteTokenId}` : null;

        // 如果传入的交易对格式正确，并且在symbol列表中不存在，并且开启KYC校验后，检查交易对是否符合KYC规则
        if (linkSymbolId && !symbol && !ignoreKYC) {
            await dispatch('GET_KYC_STATUS', linkSymbolId);
        }

        // 链接中有交易对信息，但没有匹配到时则提示如下：
        // 当前symbol在symbol列表中不存在有两种可能：
        // 可能1：交易对不符合KYC规则，被接口过滤掉，但存在；
        // 可能2：交易对确实不存在；
        if (baseTokenId && quoteTokenId && !symbol) {
            // 符合上述可能1，会弹出KYC提示层，不再向后执行，等待弹窗操作；
            if (!state.kycRule.isKyc && !ignoreKYC) return;

            // 符合上述可能2，弹出toast提示
            infoToast(i18n.global.t('toastTheSymbolNotExist'));
        }

        // 如果没有交易对且不修正交易对时则忽略
        if (!repair && !symbol) return;

        // 不存在交易对则使用缓存的交易对
        if (!symbol && symbolMap[ls.get(SPOT_LS_SYMBOL_ID)]) symbol = symbolMap[ls.get(SPOT_LS_SYMBOL_ID)];

        // 如果没有交易对，则使用第一个可以访问的交易对
        if (!symbol) [symbol] = symbols;

        // 以下几种情况重写链接 URL
        // 1. 链接中有交易对，但大小写错误，如：`/trade/spot/bit/usdt` 、`/trade/spot/bit/USDT` 、`/trade/spot/BIT/usdt`
        // 2. 首次进入，交易页还没有设置过交易对
        // 3. 切换交易对
        if (
            (baseTokenId && quoteTokenId && baseTokenId !== symbol.baseTokenId && quoteTokenId !== symbol.quoteTokenId)
            || !state.symbolId
            || state.symbolId !== symbol.symbolId
        ) {
            if (!window.sfe.prerender) {
                const { origin, search, hash } = window.location;
                const langUrl = replace(`/trade/spot/${symbol.baseTokenId}/${symbol.quoteTokenId}`);
                const url = `${origin}${langUrl}${search}${hash}`;

                window.history.replaceState(null, '', url);
            }

            // 设置symbolId
            commit('SET_SYMBOL_ID', symbol.symbolId);

            // 判断是否LT
            commit('UPDATE_IS_LT', symbol.symbolType === 'LT');

            // 判断是否全仓杠杆
            commit('leverage/UPDATE_IS_LEVERAGE', !!symbol.loanEnabled, { root: true });
        }
    },

    SET_KLINEINFO({ commit }, payload) {
        commit('SET_KLINEINFO', payload);
    },
    SET_KLINEINFO_LT({ commit }, payload) {
        commit('SET_KLINEINFO_LT', payload);
    },

    // 打开交易对的可交易状态，用于集中管理开盘后各种状态
    SYMBOL_SHOW_OPEN({ commit, rootGetters, dispatch }, symbolId) {
        const symbol = rootGetters['settings/symbolMap'][symbolId];
        // 没有交易对忽略
        if (!symbol) return;

        // 先更新下交易对数据，更新后很多组件依赖 `noticeVisible` 去初始化
        // 用于倒计时结束的调用处理
        commit('settings/SET_SYMBOL_BY_ID', {
            ...symbol,

            // 强制开启条件下单，后端接口在倒计时开盘时交易对数据会返回 false ，如果这里不开启则需要重新调用下接口。
            // Broker后台管理在倒计时开启时默认会关闭条件下单，倒计时结束时会直接打开条件下单
            allowPlan: true,

            // 直接打开页面可见状态
            showStatus: true,

            // 直接设置倒计时为 false
            noticeVisible: false,

            // 改成全量状态
            tradeStatus: 3,

            // 直接设置禁买禁卖关闭
            isBanBuy: false,
            isBanSell: false,
        }, { root: true });
    },

    // 获取 LT 风险提示状态
    async GET_LT_INFO({ commit, dispatch }) {
        // 是否确认开通
        const { ltRisk: { isConfirmOpen, isPassTest } } = state;
        if (isConfirmOpen && isPassTest) return;
        if (isConfirmOpen && !isPassTest) {
            dispatch('GET_LT_QUESTION');
            return;
        }
        const { success, result } = await ltApi.getLtRiskStatus();
        if (success) {
            commit('UPDATE_LT_RISK', !!result?.riskWarning);
            commit('UPDATE_LT_QUESTION_STATE', !!result?.questionnaireSurvey);
            if (!result.questionnaireSurvey) {
                dispatch('GET_LT_QUESTION');
            }
            commit('UPDATE_LT_LAYER', true);
        }
    },
    // 获取 LT 测试题
    async GET_LT_QUESTION({ commit }) {
        if (state.ltRisk.questions.length) {
            commit('UPDATE_LT_LAYER', true);
            return;
        }
        const res = await ltApi.getLtQuestion({ type: 2 }) || {};
        const { success, result = [] } = res;
        if (success && result.length) {
            commit('UPDATE_LT_QUESTIONS', result);
            commit('UPDATE_LT_LAYER', true);
            return Promise.resolve();
        }
    },
    // 设置 LT 产品披露信息
    async GET_LT_SYMBOL_INFO({ state, commit }) {
        const res = await ltApi.getLtSymbolInfo({ symbol: state.symbolId });
        if (res.success) {
            commit('GET_LT_SYMBOL_INFO', res.result);
            return;
        }
        commit('GET_LT_SYMBOL_INFO', {});
    },
    // 获取ST状态
    GET_ST_STATUS({
        rootGetters, rootState, state, commit,
    }) {
        const symbol = rootGetters['settings/symbolMap'][state.symbolId];
        if (!symbol.mark?.includes('ST')) return;
        // ST缓存在ls中，key为userId+基础货币名称
        commit('SET_ST_RISK', !(ls.get(`${`${rootState.user.info.id}_${symbol.baseTokenId}`}`)));
    },
    // 获取风险提示状态
    // type不填时为bit风险提示
    // type为1时为创新区风险提示
    async GET_RISK_STATUS({ commit, dispatch }, type) {
        const res = await userapi.getBitDisclaimer({ type });
        // status为0时为未同意，为1时已同意
        const status = res.result?.isKnowRisk || 0;
        if (type === 1) {
            commit('SET_INNOVATE_RISK', !status);
            commit('SET_INNOVATE_STATUS', Number(status));
            if (status) {
                // 创新区同意后需检测ST状态
                dispatch('GET_ST_STATUS');
            }
        } else {
            commit('SET_BIT_DISCLAIMER_VISIBLE', !status);
            commit('SET_BIT_DISCLAIMER', Number(status));
        }
    },
    // 获取交易对是否符合KYC
    async GET_KYC_STATUS({ commit }, symbolId) {
        const res = await commonapi.getSymbolKyc({ symbolId });
        const { code, result } = res;
        if (code === 0) {
            const { isShow, area } = result;
            commit('SET_IS_KYC', isShow);
            commit('SET_KYC_AREA', area);
        }
    },
};

const getters = {
    currentSymbol: ({ symbolId }, _, __, rootGetters) => (rootGetters['settings/symbolMap'][symbolId] || {}),
    currentQuoteBalance: (_, { currentSymbol }, __, rootGetters) => rootGetters['balance/balanceMap'][currentSymbol.quoteTokenId],
    currentBaseBalance: (_, { currentSymbol }, __, rootGetters) => rootGetters['balance/balanceMap'][currentSymbol.baseTokenId],
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};
